import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/shared/ui/toaster";
import { Toaster as Sonner } from "@/shared/ui/sonner";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import React, { Suspense, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { useAuthInitialization } from "@/features/auth/hooks/useAuthInitialization";
import { useAuthStore } from "@/features/auth/store";
import { initPushNotifications } from "@/features/notifications/utils/pushNotifications";
import { deviceApi } from "@/features/notifications/api";
import { publicApi } from "@/features/storefront/api";

// Route configurations
import { publicRoutes } from "@/app/router/publicRoutes";
import { authRoutes } from "@/app/router/authRoutes";
import { privateRoutes } from "@/app/router/privateRoutes";
import { adminRoutes } from "@/app/router/adminRoutes";
import NotFound from "@/shared/pages/NotFound";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";

// Hostnames that belong to the platform itself — custom domain detection skips these.
const PLATFORM_HOSTNAMES = new Set(['restroos.com', 'www.restroos.com', 'localhost', '127.0.0.1']);

function isPlatformHost(hostname: string): boolean {
  return (
    PLATFORM_HOSTNAMES.has(hostname) ||
    hostname.endsWith('.restroos.com') ||
    hostname.endsWith('.web.app') ||
    hostname.endsWith('.firebaseapp.com') ||
    hostname.endsWith('.run.app')
  );
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data stays fresh for 5 minutes - no refetch during this time
      staleTime: 5 * 60 * 1000,
      // Cache data for 10 minutes before garbage collection
      gcTime: 10 * 60 * 1000,
      // Only refetch on mount if data is stale
      refetchOnMount: true,
      // Disable refetch on window focus (too aggressive for restaurant data)
      refetchOnWindowFocus: false,
      // Disable refetch on reconnect
      refetchOnReconnect: false,
      // Retry failed requests once
      retry: 1,
    },
  },
});

/**
 * AppRoutes Component
 *
 * Combines public, auth, and private routes into a single route configuration.
 * Routes are separated into different files for better organization and maintainability.
 *
 * Custom domain support: when the app boots on a non-platform hostname (e.g.
 * www.rasamrestaurant.de), we resolve the restaurant by that hostname and rewrite
 * every request path to /<slug>[/rest-of-path] so the existing restaurant routes
 * handle it normally. The browser URL is updated via history.replaceState so the
 * address bar reflects the new path without a full navigation flash.
 */
const AppRoutes = () => {
  // Initialize authentication on app load (restore session from localStorage)
  useAuthInitialization();

  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const pushInitialized = useRef(false);

  // ── Custom domain boot ────────────────────────────────────────────────────
  const hostname = window.location.hostname;
  const onCustomDomain = !isPlatformHost(hostname);

  const { data: cdRestaurant, isLoading: cdLoading, isError: cdError } = useQuery({
    queryKey: ['custom-domain-boot', hostname],
    queryFn: () => publicApi.getRestaurantByCustomDomain(hostname),
    enabled: onCustomDomain,
    staleTime: Infinity,
    retry: false,
  });

  // Once the restaurant is resolved, rewrite the current path to /<slug>/...
  useEffect(() => {
    if (!onCustomDomain || cdLoading || !cdRestaurant?.slug) return;
    const slug = cdRestaurant.slug;
    const current = location.pathname;
    // Already under the correct slug prefix — no rewrite needed.
    if (current === `/${slug}` || current.startsWith(`/${slug}/`)) return;
    const subpath = current === '/' ? '' : current;
    navigate(`/${slug}${subpath}`, { replace: true });
  }, [onCustomDomain, cdLoading, cdRestaurant, location.pathname, navigate]);

  // ── Capacitor / native setup ─────────────────────────────────────────────
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Refresh stale server data when the app comes back to the foreground
    const appStateListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
    });

    // Route to the correct screen when the app is opened from a deep link
    const urlListener = CapacitorApp.addListener('appUrlOpen', (event) => {
      // restaurantos://app/restaurants/xyz/orders → /app/restaurants/xyz/orders
      const path = event.url.replace(/^[a-z]+:\/\//, '/').replace(/^\/\//, '/');
      if (path && path !== '/') navigate(path);
    });

    return () => {
      appStateListener.then((h) => h.remove());
      urlListener.then((h) => h.remove());
    };
  }, [navigate]);

  // Register for push notifications once per session after the user authenticates.
  // Guarded by a ref so Firebase's hourly token refresh (which updates `token`)
  // does not re-run registration and create duplicate device_tokens entries.
  useEffect(() => {
    if (!token) {
      pushInitialized.current = false;
      return;
    }
    if (pushInitialized.current) return;
    pushInitialized.current = true;

    initPushNotifications({
      navigate,
      onToken: async (fcmToken, platform) => {
        // Use Capacitor Preferences (not localStorage) so the ID survives app
        // reinstalls on iOS. localStorage is wiped on reinstall, which would
        // create a new Firestore device_token doc each time and cause duplicate pushes.
        let { value: deviceId } = await Preferences.get({ key: 'push-device-id' });
        if (!deviceId) {
          deviceId = crypto.randomUUID();
          await Preferences.set({ key: 'push-device-id', value: deviceId });
        }
        await deviceApi.register({ fcmToken, platform, deviceId });
        useAuthStore.getState().setDeviceId(deviceId);
      },
    });
  }, [token, navigate]);

  // Block rendering until the custom domain restaurant is resolved so the wrong
  // route (e.g. /:restaurantId matching "menu") never flashes on screen.
  if (onCustomDomain && cdLoading) {
    return <div className="min-h-screen bg-background" />;
  }

  // Custom domain found but backend returned an error — show a plain 404 rather
  // than the platform marketing page, which would be confusing.
  if (onCustomDomain && cdError) {
    return <NotFound />;
  }

  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Routes>
        {/* Exact routes first - auth and app must precede /:restaurantId catch-all */}
        {authRoutes}
        {adminRoutes}
        {privateRoutes}

        {/* Public routes - Index and restaurant pages (/:restaurantId) */}
        {publicRoutes}

        {/* 404 Not Found - must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </HelmetProvider>
);

export default App;